import type { AssetRow, DeltaResponse, EditRequest, FolderRow, JobRow } from "@loumy/contracts";

// §8/D47 — typisierter Client über die EINE interne API. Pur-API-only Daten-Plane:
// kein direkter Browser→DB-Zugriff; alle Reads/Submit laufen über `/v1/*`. Das JWT
// (Supabase-Identität) kommt per Bearer-Header; der Worker scoped service-role auf
// `userId`. DTO-Formen = die contracts-Row-Schemas (Parse-Boundary serverseitig).

export interface SubmitJobInput {
  toolId: string;
  settings: unknown;
  inputs?: { assetId: string; role: string }[];
  targetFolderId?: string | null;
  /** §11/D143: ohne expliziten Zielordner serverseitig in den tool-Default-Ordner einsortieren. */
  autoFolder?: boolean;
}
export interface SubmitJobResult {
  jobId: string;
  estimatedCredits: number;
  breakdown: { label: string; credits: number }[];
}

// Job-Detail (§11/D65) — die Server-View von `GET /v1/jobs/:id`. `downloadUrl` ist eine
// kurzlebige presigned-GET-URL (D56) oder null (kein r2_key / R2-Creds fehlen → Platzhalter, D43).
export interface JobOutputView {
  asset: AssetRow;
  downloadUrl: string | null;
}
export interface JobInputView extends JobOutputView {
  role: string;
}
export interface JobDetail {
  job: JobRow;
  inputs: JobInputView[];
  outputs: JobOutputView[];
}

// Server-View einer Asset-Liste (`/v1/assets`, D100/D142): die Zeilen + presignte Anzeige-Maps.
// Auch die Form, in der der Delta-Sync (D149) die Asset-Caches je Ordner führt.
export interface AssetsView {
  assets: AssetRow[];
  downloadUrls: Record<string, string | null>;
  titles: Record<string, string | null>;
  inputImageUrls: Record<string, string | null>;
}

// API-Key-Anzeige-Form (§9.16): NIE der Hash, NIE der Klartext (außer Einmal-Mint-Response).
export interface ApiKeyView {
  id: string;
  keyPrefix: string;
  name: string | null;
  status: "active" | "revoked";
  rateLimitRpm: number;
  monthlyQuotaCredits: number | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const BASE = import.meta.env.VITE_API_BASE_URL ?? "";

/** Liefert das aktuelle Access-Token (oder null) — vom SessionProvider gesetzt. */
type TokenGetter = () => string | null;
let getToken: TokenGetter = () => null;
export function setTokenGetter(fn: TokenGetter): void {
  getToken = fn;
}

/** Fehler-Response → ApiError (code/message aus dem JSON-Body, sonst Status-Text). */
async function assertOk(res: Response): Promise<void> {
  if (res.ok) return;
  let code = "error";
  let message = res.statusText;
  try {
    const body = (await res.json()) as { error?: string; message?: string };
    code = body.error ?? code;
    message = body.message ?? message;
  } catch {
    /* nicht-JSON Fehler → Status-Text */
  }
  throw new ApiError(res.status, code, message);
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  await assertOk(res);
  return (await res.json()) as T;
}

/**
 * User-Input-Upload (§9.4/D44/D61): der Datei-Body geht ROH an `/v1/uploads` (NICHT JSON);
 * der Worker validiert MIME/Magic-Bytes/Größe/Checksum inline und materialisiert das Asset.
 * `folderId=null` ⇒ Inbox/untargeted. Liefert die contract-validierte Asset-Zeile zurück.
 */
async function uploadAsset(file: File, folderId: string | null): Promise<AssetRow> {
  const token = getToken();
  const res = await fetch(`${BASE}/v1/uploads?folder=${folderId ?? "inbox"}`, {
    method: "POST",
    headers: {
      "content-type": file.type || "application/octet-stream",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      // Header-Werte müssen latin1-safe sein → der Dateiname wird URL-kodiert übertragen.
      ...(file.name ? { "x-filename": encodeURIComponent(file.name) } : {}),
    },
    body: file,
  });
  await assertOk(res);
  return ((await res.json()) as { asset: AssetRow }).asset;
}

// Browser-Edit-Ergebnis (§9.17/D124): chargedCredits = serverseitig fail-closed berechneter
// Preis; replayed = idempotenter Retry (existierendes Asset, nichts erneut abgebucht).
export interface SubmitEditResult {
  asset: AssetRow;
  chargedCredits: number;
  replayed: boolean;
}

/** UTF-8-JSON → base64 (latin1-safe Header-Wert für `x-edit-meta`). */
function encodeEditMeta(meta: EditRequest): string {
  const bytes = new TextEncoder().encode(JSON.stringify(meta));
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

/**
 * Browser-Edit speichern (§9.17/D124): die fertigen PNG-Bytes gehen ROH an `/v1/edits`
 * (D61-Bytes-Strecke), die Metadaten (editKind/source/params/opsHash/packMeta) als
 * base64-JSON im `x-edit-meta`-Header. Preis + Charge entstehen ausschließlich serverseitig.
 */
async function submitEdit(png: Uint8Array, meta: EditRequest, signal?: AbortSignal): Promise<SubmitEditResult> {
  const token = getToken();
  const res = await fetch(`${BASE}/v1/edits`, {
    method: "POST",
    headers: {
      "content-type": "image/png",
      "x-edit-meta": encodeEditMeta(meta),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: png.slice().buffer as ArrayBuffer,
    // Codex-P1 (D130-R1): Schließen/Unmount bricht auch den laufenden POST ab. Hat der
    // Server schon materialisiert, fängt der Idempotenz-Key einen späteren Retry (0 cr).
    signal,
  });
  await assertOk(res);
  return (await res.json()) as SubmitEditResult;
}

/** Bytes → base64 (Seam-Frame-JSON-Felder; chunked gegen Call-Stack-Limits). */
function bytesToBase64(bytes: Uint8Array): string {
  let bin = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

/**
 * D130 — RIFE-Naht-Frame für den Browser-Loop-Fix: GENAU 1 Zwischenframe neuer-End→Start
 * vom Server-Proxy (`POST /v1/edits/seam-frame`; RIFE ist ML-extern und läuft nicht im
 * Browser). Unberechnet — der Charge erfolgt beim Speichern (§9.17).
 */
async function seamFrame(
  sourceAssetId: string,
  endFrame: Uint8Array,
  startFrame: Uint8Array,
  signal?: AbortSignal,
): Promise<Uint8Array> {
  const token = getToken();
  const res = await fetch(`${BASE}/v1/edits/seam-frame`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      sourceAssetId,
      endFrame: bytesToBase64(endFrame),
      startFrame: bytesToBase64(startFrame),
    }),
    signal,
  });
  await assertOk(res);
  return new Uint8Array(await res.arrayBuffer());
}

/**
 * Asset-Bytes über den Worker-Proxy (D124): liefert CORS-fähige Bytes (presigned-R2-URLs
 * tragen keine CORS-Header → `getImageData`/Pixel-Lesen wäre tainted). Nur eigene Assets (404 sonst).
 */
async function assetBytes(assetId: string, signal?: AbortSignal): Promise<Blob> {
  const token = getToken();
  const res = await fetch(`${BASE}/v1/assets/${encodeURIComponent(assetId)}/bytes`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
    // D131-Codex-P2: Unmount des Aufrufers (z. B. LoopFixEditor) bricht den laufenden Fetch ab.
    signal,
  });
  await assertOk(res);
  return await res.blob();
}

export const api = {
  listFolders: () => call<{ folders: FolderRow[] }>("/v1/folders").then((r) => r.folders),
  // D100: presigned Output-Vorschau-URLs je Asset. D142: zusätzlich `titles` (Auto-Titel des
  // Ursprungs-Jobs) und `inputImageUrls` (presigned URL des EINGABE-Bilds). Alle fail-soft —
  // ein noch nicht deployter Worker ohne die Felder → leere Maps → Fallback wie bisher (D43).
  listAssets: (folderId: string | null) =>
    call<{
      assets: AssetRow[];
      downloadUrls?: Record<string, string | null>;
      titles?: Record<string, string | null>;
      inputImageUrls?: Record<string, string | null>;
    }>(`/v1/assets?folder=${folderId ?? "inbox"}`).then((r) => ({
      assets: r.assets,
      downloadUrls: r.downloadUrls ?? {},
      titles: r.titles ?? {},
      inputImageUrls: r.inputImageUrls ?? {},
    })),
  listJobs: () => call<{ jobs: JobRow[] }>("/v1/jobs").then((r) => r.jobs),
  // §11/D155: presigntes Vorschau-Bild je Job (Eingabe-Bild Vorrang, Output-Fallback) für die
  // Jobs-Liste — ein FERTIGER Job zeigt sein Bild wie die FolderTree-Asset-Zeilen (D142). Eigene
  // Route (qk.jobs/Delta bleiben JobRow[]). Fail-soft: ein alter Worker ohne die Route → leere Map.
  listJobPreviews: () =>
    call<{ previewUrls?: Record<string, string | null> }>("/v1/jobs/previews").then((r) => r.previewUrls ?? {}),
  // Job-Detail (§11/D65): Job-Zeile + Inputs (mit role) + Outputs, je mit presigned downloadUrl.
  // Boundary-Normalisierung: ein (noch) nicht D65-deployter api-worker liefert nur `{job}` —
  // `inputs`/`outputs` auf `[]` defaulten, damit Konsumenten nie über `undefined` iterieren
  // (Settings-Rehydrierung funktioniert dann schon, Inputs/Outputs kommen mit dem Backend-Deploy).
  getJob: (id: string) =>
    call<JobDetail>(`/v1/jobs/${encodeURIComponent(id)}`).then((d) => ({
      job: d.job,
      inputs: d.inputs ?? [],
      outputs: d.outputs ?? [],
    })),
  credits: () => call<{ available: number }>("/v1/credits").then((r) => r.available),
  // §8/D149 — Delta-Sync: nur die seit `since` geänderten Zeilen (jobs/folders/assets-Δ + credits +
  // nextSince). `since=null` ⇒ Voll-Abzug (Cursor-Seed beim Erstabruf). Ersetzt das Reconnect-
  // Blind-Invalidate; der Client merged das Ergebnis in den persistierten Cache (state/deltaSync).
  delta: (since: string | null) =>
    call<DeltaResponse>(`/v1/delta${since ? `?since=${encodeURIComponent(since)}` : ""}`),
  // Live-Status-Channel-Token (F-5/D47): kurzlebiges WS/SSE-Token; das JWT geht nur im
  // Authorization-Header an /v1/live-token, nie in den WS-Query-String.
  liveToken: () => call<{ token: string; expiresAt: number }>("/v1/live-token", { method: "POST" }).then((r) => r.token),
  submitJob: (input: SubmitJobInput) =>
    call<SubmitJobResult>("/v1/jobs", { method: "POST", body: JSON.stringify(input) }),
  // User-Input-Upload (§9.4/D44/D61) — Roh-Body, server-validiert + materialisiert.
  uploadAsset,
  // Browser-Edit (§9.17/D124) — PNG-Roh-Body + x-edit-meta; atomarer Charge serverseitig.
  submitEdit,
  // RIFE-Naht-Frame für den Browser-Loop-Fix (§9.17/D130).
  seamFrame,
  // CORS-fähige Asset-Bytes über den Worker-Proxy (D124, Edit-Quellen).
  assetBytes,
  // Promo-Code einlösen (§9.15/D54) — atomar via redeem_code-RPC; gibt das neue Saldo zurück.
  // `idempotencyKey` (user-scoped, §9.15) schützt einen Einlöse-Versuch gegen Netz-Retry/Doppel-Submit.
  redeemCode: (code: string, idempotencyKey?: string) =>
    call<{ available: number }>("/v1/codes/redeem", {
      method: "POST",
      body: JSON.stringify({ code, idempotencyKey }),
    }),
  // Folder-/Asset-Mutationen (§9.1/§11/D100) — Ownership/Locks/Zyklus-Schutz serverseitig (RPCs D40/D44).
  createFolder: (name: string, parentId: string | null) =>
    call<{ folder: FolderRow }>("/v1/folders", {
      method: "POST",
      body: JSON.stringify({ name, parentId }),
    }).then((r) => r.folder),
  renameFolder: (id: string, name: string) =>
    call<{ ok: true }>(`/v1/folders/${encodeURIComponent(id)}/rename`, {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  moveFolder: (id: string, parentId: string | null) =>
    call<{ ok: true }>(`/v1/folders/${encodeURIComponent(id)}/move`, {
      method: "POST",
      body: JSON.stringify({ parentId }),
    }),
  trashFolder: (id: string) =>
    call<{ ok: true }>(`/v1/folders/${encodeURIComponent(id)}/trash`, { method: "POST", body: "{}" }),
  moveAsset: (id: string, folderId: string | null) =>
    call<{ ok: true }>(`/v1/assets/${encodeURIComponent(id)}/move`, {
      method: "POST",
      body: JSON.stringify({ folderId }),
    }),
  // Job-Eintrag → Ordner/Inbox ziehen (D100): verschiebt ALLE Ergebnis-Assets des Jobs.
  moveJobOutputs: (id: string, folderId: string | null) =>
    call<{ moved: number }>(`/v1/jobs/${encodeURIComponent(id)}/move-outputs`, {
      method: "POST",
      body: JSON.stringify({ folderId }),
    }),
  // API-Keys (§9.16 Account-Surface): der Klartext kommt GENAU EINMAL in der Mint-Response
  // (nie gespeichert/erneut abrufbar — die Liste zeigt nur den keyPrefix).
  listApiKeys: () => call<{ keys: ApiKeyView[] }>("/v1/api-keys").then((r) => r.keys),
  createApiKey: (name?: string) =>
    call<{ key: ApiKeyView; plaintext: string }>("/v1/api-keys", {
      method: "POST",
      body: JSON.stringify(name ? { name } : {}),
    }),
  revokeApiKey: (id: string) =>
    call<{ key: ApiKeyView }>(`/v1/api-keys/${encodeURIComponent(id)}/revoke`, { method: "POST", body: "{}" }).then(
      (r) => r.key,
    ),
};
